import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../store/auth.store'

export type RegulatoryFrequency =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'ANNUAL'

export type RegulatoryEventType = 'REGULATORY' | 'REMINDER' | 'NOTE' | 'EXTERNAL_EMAIL'

export type EventSeverity = 'CRITICO_MAXIMO' | 'CRITICO' | 'ALTO' | 'MEDIO' | 'BAJO' | 'INFORMATIVO'

export type RecipientKind = 'INTERNAL' | 'EXTERNAL'

export interface RegulatoryEntity {
  id: string
  name: string
  acronym?: string | null
  description?: string | null
  _count?: { events: number }
}

export interface CalendarRecipientUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

export interface CalendarRecipient {
  id: string
  kind: RecipientKind
  userId?: string | null
  user?: CalendarRecipientUser | null
  externalEmail?: string | null
  externalName?: string | null
  delivered?: boolean
  reminded?: boolean
}

export interface CalendarAttachment {
  id: string
  filename: string
  url?: string | null
  localPath?: string | null
  mimeType?: string | null
  size?: number | null
}

export interface CalendarUser {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
}

export interface RegulatoryEvent {
  id: string
  title: string
  description?: string | null
  frequency: RegulatoryFrequency
  startDate: string
  endDate?: string | null
  startTime?: string | null
  endTime?: string | null
  nextDueDate: string
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'CANCELLED'
  computedStatus?: string
  type: RegulatoryEventType
  sendEmail?: boolean
  isPersonalNote?: boolean
  sendReminderToAllUsers?: boolean
  severity?: EventSeverity
  entityId?: string | null
  entity?: RegulatoryEntity | null
  recipients?: CalendarRecipient[]
  attachments?: CalendarAttachment[]
  notes?: string | null
}

export function useRegulatoryEntities() {
  const api = useApi()
  return useQuery<RegulatoryEntity[]>({
    queryKey: ['regulatory-entities'],
    queryFn: () => api.get('/regulatory-calendar/entities'),
    staleTime: 1000 * 60 * 10,
  })
}

export function useCalendarUsers() {
  const api = useApi()
  return useQuery<CalendarUser[]>({
    queryKey: ['calendar-users'],
    queryFn: () => api.get('/regulatory-calendar/users'),
    staleTime: 1000 * 60 * 10,
  })
}

export function useRegulatoryEvents(filters?: {
  type?: string
  entityId?: string
  status?: string
  severity?: string
  start?: string
  end?: string
  search?: string
}) {
  const api = useApi()
  const params = new URLSearchParams()
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  const query = params.toString()
  return useQuery<RegulatoryEvent[]>({
    queryKey: ['regulatory-events', filters],
    queryFn: () => api.get(`/regulatory-calendar${query ? `?${query}` : ''}`),
    refetchInterval: 30000,
  })
}

export function useRegulatoryStats() {
  const api = useApi()
  return useQuery<{
    total: number
    overdue: number
    dueThisMonth: number
    completed: number
    byType?: Record<string, number>
    bySeverity?: Record<string, number>
  }>({
    queryKey: ['regulatory-stats'],
    queryFn: () => api.get('/regulatory-calendar/stats'),
    refetchInterval: 30000,
  })
}

export function useCreateRegulatoryEvent() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: any) => api.post('/regulatory-calendar', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-events'], exact: false })
      qc.invalidateQueries({ queryKey: ['regulatory-stats'] })
      qc.invalidateQueries({ queryKey: ['regulatory-entities'] })
    },
  })
}

export function useUpdateRegulatoryEvent() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => api.patch(`/regulatory-calendar/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-events'], exact: false })
      qc.invalidateQueries({ queryKey: ['regulatory-stats'] })
    },
  })
}

export function useCompleteRegulatoryEvent() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/regulatory-calendar/${id}/complete`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-events'], exact: false })
      qc.invalidateQueries({ queryKey: ['regulatory-stats'] })
    },
  })
}

export function useDeleteRegulatoryEvent() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/regulatory-calendar/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-events'], exact: false })
      qc.invalidateQueries({ queryKey: ['regulatory-stats'] })
    },
  })
}

export const FREQUENCY_LABELS: Record<RegulatoryFrequency, string> = {
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quincenal',
  MONTHLY: 'Mensual',
  BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
}

export const FREQUENCY_OPTIONS: RegulatoryFrequency[] = [
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'BIMONTHLY',
  'QUARTERLY',
  'SEMIANNUAL',
  'ANNUAL',
]

export const EVENT_TYPE_LABELS: Record<RegulatoryEventType, string> = {
  REGULATORY: 'Obligación regulatoria',
  REMINDER: 'Recordatorio interno',
  NOTE: 'Nota personal',
  EXTERNAL_EMAIL: 'Correo externo',
}

export const EVENT_TYPE_DESCRIPTIONS: Record<RegulatoryEventType, string> = {
  REGULATORY: 'Obligación con entidad regulatoria (DIAN, UGPP, etc.)',
  REMINDER: 'Recordatorio para usuarios internos de la app',
  NOTE: 'Nota personal, solo para tu referencia',
  EXTERNAL_EMAIL: 'Correo a destinatarios fuera de la app',
}

export const SEVERITY_OPTIONS: EventSeverity[] = ['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO']

export const SEVERITY_LABELS: Record<EventSeverity, string> = {
  CRITICO_MAXIMO: 'Crítico Máximo',
  CRITICO: 'Crítico',
  ALTO: 'Alto',
  MEDIO: 'Medio',
  BAJO: 'Bajo',
  INFORMATIVO: 'Informativo',
}

// Etiquetas cortas para badges
export const SEVERITY_SHORT_LABELS: Record<EventSeverity, string> = {
  CRITICO_MAXIMO: 'Crít. Máx',
  CRITICO: 'Crítico',
  ALTO: 'Alto',
  MEDIO: 'Medio',
  BAJO: 'Bajo',
  INFORMATIVO: 'Info',
}

export const SEVERITY_COLORS: Record<EventSeverity, string> = {
  CRITICO_MAXIMO: 'bg-red-100 text-red-700 border-red-300',
  CRITICO: 'bg-red-50 text-red-700 border-red-200',
  ALTO: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIO: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  BAJO: 'bg-green-50 text-green-700 border-green-200',
  INFORMATIVO: 'bg-slate-100 text-slate-600 border-slate-200',
}

export const SEVERITY_BADGE: Record<EventSeverity, string> = {
  CRITICO_MAXIMO: 'bg-red-600 text-white',
  CRITICO: 'bg-red-500 text-white',
  ALTO: 'bg-orange-500 text-white',
  MEDIO: 'bg-yellow-500 text-white',
  BAJO: 'bg-green-500 text-white',
  INFORMATIVO: 'bg-slate-500 text-white',
}

export const SEVERITY_RANK: Record<EventSeverity, number> = {
  CRITICO_MAXIMO: 5,
  CRITICO: 4,
  ALTO: 3,
  MEDIO: 2,
  BAJO: 1,
  INFORMATIVO: 0,
}